import { useState, useEffect, useRef } from "react";

interface TypewriterTextProps {
  text: string;
  speed?: number; // Characters per second
  onComplete?: () => void;
  className?: string;
}

/**
 * TypewriterText - Displays text with a streaming/typewriter effect.
 * Used for AI assistant messages to provide visual feedback during generation.
 */
export function TypewriterText({
  text,
  speed = 60,
  onComplete,
  className = "",
}: TypewriterTextProps) {
  const [displayedText, setDisplayedText] = useState("");
  const [isComplete, setIsComplete] = useState(false);
  const indexRef = useRef(0);
  const prevTextRef = useRef("");

  useEffect(() => {
    // Reset when text changes completely (new message)
    if (text !== prevTextRef.current && !text.startsWith(prevTextRef.current)) {
      indexRef.current = 0;
      setDisplayedText("");
      setIsComplete(false);
    }
    prevTextRef.current = text;

    if (indexRef.current >= text.length) {
      if (!isComplete) {
        setIsComplete(true);
        onComplete?.();
      }
      return;
    }

    const intervalMs = 1000 / speed;
    const timer = setInterval(() => {
      if (indexRef.current < text.length) {
        // Add multiple characters per tick for faster display
        const charsToAdd = Math.min(3, text.length - indexRef.current);
        indexRef.current += charsToAdd;
        setDisplayedText(text.slice(0, indexRef.current));
      } else {
        clearInterval(timer);
        if (!isComplete) {
          setIsComplete(true);
          onComplete?.();
        }
      }
    }, intervalMs);

    return () => clearInterval(timer);
  }, [text, speed, isComplete, onComplete]);

  return (
    <span className={className}>
      {displayedText}
      {!isComplete && <span className="animate-pulse">|</span>}
    </span>
  );
}

export default TypewriterText;
